import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Upload, Trash2, ArrowLeft, Video, Sparkles, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/content/landing";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

const CTA_PRESETS = [
  { label: "Join My Team",     url: "/opportunities?type=affiliate" },
  { label: "WhatsApp Me",      url: "" },
  { label: "View Opportunity", url: "/opportunities" },
  { label: "Become a Vendor",  url: "/vendor" },
  { label: "Learn More",       url: "" },
];

export default function PostPromo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Learn More");
  const [ctaUrl, setCtaUrl] = useState("");
  const [brandTag, setBrandTag] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/promos/mine`, { withCredentials: true })
      .then((r) => setMine(r.data || []))
      .catch(() => {});
  }, [submitting]);

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["video/mp4", "video/webm", "video/quicktime"].includes(f.type)) {
      toast.error("Please choose an MP4, WebM or MOV file.");
      return;
    }
    if (f.size > 80 * 1024 * 1024) {
      toast.error("Video too large (max 80 MB).");
      return;
    }
    setVideoFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!videoFile) return toast.error("Pick a video first.");
    if (title.trim().length < 2) return toast.error("Add a title.");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", videoFile);
    fd.append("title", title.trim());
    fd.append("caption", caption.trim());
    fd.append("cta_label", ctaLabel);
    fd.append("cta_url", ctaUrl.trim());
    fd.append("brand_tag", brandTag.trim().replace(/^#/, ""));
    try {
      await axios.post(`${BACKEND}/api/promos`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Your promo is live on Opportunity TV.");
      setVideoFile(null);
      setPreview("");
      setTitle("");
      setCaption("");
      setBrandTag("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (promo_id) => {
    if (!window.confirm("Delete this promo?")) return;
    try {
      await axios.delete(`${BACKEND}/api/promos/${promo_id}`, { withCredentials: true });
      setMine((prev) => prev.filter((p) => p.promo_id !== promo_id));
      toast.success("Promo deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* header */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1.5" data-testid="post-promo-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <h1 className="font-display font-black text-2xl tracking-tight mt-1">Post a Promo</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Reach Malaysians on Opportunity TV with a short, vertical video.</p>
          </div>
          <button
            onClick={() => navigate("/tv")}
            className="hidden sm:inline-flex items-center gap-2 text-white px-4 py-2 text-sm font-semibold hover:opacity-90"
            style={{ background: CHARCOAL }}
            data-testid="post-promo-open-tv"
          >
            <Play className="w-4 h-4" /> Open Opportunity TV
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* form */}
        <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 lg:p-8 space-y-6" data-testid="post-promo-form">
          {/* uploader */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2">1. Pick a vertical video</label>
            <div className="flex flex-col sm:flex-row gap-5">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="relative w-44 h-72 bg-zinc-900 text-white grid place-items-center border-2 border-dashed border-zinc-300 hover:border-[#C8102E] overflow-hidden flex-shrink-0"
                data-testid="post-promo-pick-video"
              >
                {preview ? (
                  <video src={preview} className="absolute inset-0 w-full h-full object-cover" muted autoPlay loop playsInline />
                ) : (
                  <div className="text-center px-4">
                    <Video className="w-10 h-10 mx-auto mb-3 opacity-70" />
                    <div className="text-sm font-semibold">Tap to upload</div>
                    <div className="text-[10px] text-zinc-400 mt-1">MP4 / WebM / MOV · 80MB max</div>
                  </div>
                )}
              </button>
              <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onSelect} className="hidden" data-testid="post-promo-file-input" />
              <div className="text-xs text-zinc-600 leading-relaxed space-y-2 max-w-sm">
                <p><strong className="text-zinc-900">Best practice:</strong> Shoot vertical (9:16) like TikTok.</p>
                <p>Keep it under 60 seconds. Hook in the first 2 seconds. End with a clear ask.</p>
                <p>Show your product, demo earnings, or share your story.</p>
              </div>
            </div>
          </div>

          {/* title */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2">2. Hook title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. How I made RM3,000/month from home with INOCHI"
              className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-[#C8102E] focus:outline-none"
              data-testid="post-promo-title"
              required
            />
          </div>

          {/* caption */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2">3. Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Tell viewers what they get and how to start. Keep it punchy."
              className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-[#C8102E] focus:outline-none resize-none"
              data-testid="post-promo-caption"
            />
            <div className="text-[10px] text-zinc-400 mt-1 text-right">{caption.length}/500</div>
          </div>

          {/* CTA */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2">4. Call-to-action</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {CTA_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setCtaLabel(p.label); setCtaUrl(p.url); }}
                  className={`text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 border ${ctaLabel === p.label ? "bg-[#09090b] text-white border-[#09090b]" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}
                  data-testid={`post-promo-cta-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                maxLength={40}
                placeholder="Button label"
                className="border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#C8102E] focus:outline-none"
                data-testid="post-promo-cta-label"
              />
              <input
                type="text"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                maxLength={500}
                placeholder="Link (https://wa.me/... or /opportunities)"
                className="border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#C8102E] focus:outline-none"
                data-testid="post-promo-cta-url"
              />
            </div>
          </div>

          {/* brand tag */}
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2">5. Brand tag (optional)</label>
            <input
              type="text"
              value={brandTag}
              onChange={(e) => setBrandTag(e.target.value)}
              maxLength={40}
              placeholder="inochi, soupway, nano-shield…"
              className="w-full border border-zinc-300 px-4 py-3 text-sm focus:border-[#C8102E] focus:outline-none"
              data-testid="post-promo-brand-tag"
            />
          </div>

          {/* submit */}
          <div className="pt-2 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={submitting || !videoFile}
              className="inline-flex items-center gap-2 text-white px-6 py-3 font-bold text-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: RED }}
              data-testid="post-promo-submit"
            >
              {submitting ? "Uploading…" : <>Go Live <Sparkles className="w-4 h-4" /></>}
            </button>
            <span className="text-[11px] text-zinc-500">Your promo appears on Opportunity TV instantly.</span>
          </div>
        </form>

        {/* my promos sidebar */}
        <aside className="space-y-4" data-testid="post-promo-mine-list">
          <div className="bg-white border border-zinc-200 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Posting as</div>
            <div className="font-bold text-sm mt-1 text-zinc-900">{user?.name || user?.email}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-widest">{user?.role?.replace("_", " ") || "—"}</div>
          </div>

          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">My Promos</div>
              <div className="text-sm font-bold mt-0.5">{mine.length} {mine.length === 1 ? "video" : "videos"} live</div>
            </div>
            {mine.length === 0 ? (
              <div className="p-5 text-xs text-zinc-500 leading-relaxed">No promos yet. Your first upload appears here and on the public feed.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {mine.map((p) => (
                  <li key={p.promo_id} className="px-5 py-3 flex items-center gap-3" data-testid={`mine-promo-${p.promo_id}`}>
                    <video src={`${BACKEND}${p.video_url}`} muted className="w-12 h-16 object-cover bg-black flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{p.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{p.likes || 0} likes · {p.views || 0} views</div>
                    </div>
                    <button onClick={() => remove(p.promo_id)} className="text-zinc-400 hover:text-[#C8102E]" data-testid={`mine-promo-delete-${p.promo_id}`} aria-label="Delete promo">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
